import { Checkbox } from '@ark-ui/react/checkbox';
import { Square, SquareCheck } from 'lucide-react';
import { type FC, type ReactNode } from 'react';

/** The weight the redesign draws its lucide marks at. */
const ICON_STROKE_WIDTH = 1.75;

/*
 * Ark's Checkbox: the design system exports no checkbox, and the one in
 * @consigli/facade belongs to the legacy stack the redesign moves away from.
 *
 * The frames draw the two states as whole glyphs — `Icon / square` and
 * `Icon / square-check` — rather than a box with a tick inside it, so both are
 * rendered and swapped on the control's `data-state`. `group` goes on the
 * control because that is the element Ark stamps the state onto.
 */
export const InviteCheckboxControl: FC = () => (
  <Checkbox.Control className="invite-check group flex size-4 shrink-0 items-center justify-center">
    <Square
      aria-hidden="true"
      className="size-4 text-[var(--ds-color-raw-stone-600)] group-data-[state=checked]:hidden"
      strokeWidth={ICON_STROKE_WIDTH}
    />
    <SquareCheck
      aria-hidden="true"
      className="invite-check-on hidden size-4 group-data-[state=checked]:block"
      strokeWidth={ICON_STROKE_WIDTH}
    />
  </Checkbox.Control>
);

interface InviteCheckboxProps {
  checked: boolean;
  /** True while some but not all of the group is ticked. */
  indeterminate?: boolean;
  label: ReactNode;
  onCheckedChange: (checked: boolean) => void;
}

/**
 * Box plus visible label, for the agent list's Select All.
 *
 * The label is 13/18 in the frame, which is the one place the panel drops below
 * 14 — the agent rows beside it stay at 14. Read off the export rather than
 * rounded to the nearest utility.
 */
export const InviteCheckbox: FC<InviteCheckboxProps> = ({
  checked,
  indeterminate = false,
  label,
  onCheckedChange,
}) => (
  <Checkbox.Root
    // Ark's own tri-state value. `indeterminate` is what puts `mixed` on the
    // input's aria-checked, which is the only way a partly-ticked Select All
    // says so to a screen reader.
    //
    // It draws as the empty square, because the frame gives the control two
    // states and no mixed mark. That is the same square a screen-reader user
    // hears as "mixed", so the two do not disagree — there is simply no glyph
    // to draw for it, and inventing one is a design call rather than this
    // component's.
    checked={indeterminate ? 'indeterminate' : checked}
    className="flex shrink-0 cursor-pointer items-center gap-2"
    // `next` is `true` from the indeterminate state, which is the behaviour the
    // group wants: a partial selection ticks the rest rather than clearing it.
    onCheckedChange={({ checked: next }) => onCheckedChange(next === true)}
  >
    <Checkbox.HiddenInput />
    <InviteCheckboxControl />
    <Checkbox.Label className="whitespace-nowrap text-[13px] leading-[18px] text-[var(--ds-color-text-primary)]">
      {label}
    </Checkbox.Label>
  </Checkbox.Root>
);
